// eDOS · Search By Test Name. Big search input on top — filters live
// across name, code, aliases, parameters. Results render through the
// shared EdosResultsList atom so all three search modes feel like one
// surface with a different picker.
//
// Data is now sourced from the eDOS Laravel API:
//   - Default state → /get-edos/testsList (paginated catalogue)
//   - With a query  → /get-edos/search?typewords=... (multi-field search)
// Both are debounced (350 ms) so each keystroke doesn't fire a request,
// and pagination is per-mode (resets when the search query changes).
import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';
import { MP, MONO, DISPLAY } from '../medical/shared';
import { useGetTestsListQuery, useSearchTestsQuery } from '../../../store/edosApi';
import { mapApiToTestRow } from '../../../utils/edosLabAdapter';
import EdosResultsList from './EdosResultsList';
import EdosTestDetail from './EdosTestDetail';

const QUICK_SEARCHES = ['CBC', 'Lipid', 'Thyroid', 'Vitamin D', 'HbA1c', 'CRP'];
const PER_PAGE = 10;

const EdosTests = () => {
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTest, setSelectedTest] = useState(null);

  // Debounce the keystrokes — 350 ms after the user stops typing, the
  // committed search term updates and the API fetch fires. Resets the
  // page back to 1 whenever the search term changes so we never show
  // page 5 of one query against page 1 of the next.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const isSearching = debouncedQ.length > 0;

  // Default catalogue (paginated) when the user hasn't typed anything.
  const listQuery = useGetTestsListQuery(
    { page, perPage: PER_PAGE },
    { skip: isSearching },
  );

  // Search endpoint — paginated, multi-field LIKE on the backend.
  const searchQuery = useSearchTestsQuery(
    { typewords: debouncedQ, page, perPage: PER_PAGE },
    { skip: !isSearching },
  );

  const active = isSearching ? searchQuery : listQuery;
  const tests = useMemo(
    () => (active.data?.items || []).map(mapApiToTestRow).filter(Boolean),
    [active.data],
  );
  const pagination = active.data?.pagination || null;

  // Detail view takes over the page when a test is selected.
  // Going back restores the search input + result state intact.
  if (selectedTest) {
    return (
      <EdosTestDetail
        test={selectedTest}
        onBack={() => setSelectedTest(null)}
        onSelectTest={setSelectedTest}
        sectionLabel="Search By Test Name"
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Picker hero — search input + quick chips */}
      <Box
        sx={{
          bgcolor: MP.white,
          border: `1px solid ${MP.border}`,
          borderRadius: '6px',
          boxShadow: MP.shadow,
          p: { xs: 2.5, md: 3.5 },
        }}
      >
        <Typography
          sx={{
            fontFamily: MONO,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: MP.tealDark,
            mb: 1,
          }}
        >
          eDOS · Search By Test Name
        </Typography>
        <Typography
          sx={{
            fontFamily: DISPLAY,
            fontSize: { xs: 22, md: 28 },
            fontWeight: 700,
            color: MP.dark,
            letterSpacing: '0.5px',
            mb: 0.75,
          }}
        >
          Find any test in our directory
        </Typography>
        <Typography sx={{ fontSize: 13, color: MP.textMuted, mb: 2.25, maxWidth: '60ch' }}>
          Search by test name, short code, alias, or any parameter — partner pricing
          appears alongside MRP in the results below.
        </Typography>

        {/* Search input */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1.5px solid ${q ? MP.teal : MP.border}`,
            borderRadius: '6px',
            bgcolor: MP.bg,
            transition: 'border-color 0.18s, background 0.18s',
            '&:focus-within': { borderColor: MP.teal, bgcolor: MP.white },
          }}
        >
          <Box sx={{ pl: 2, display: 'flex', alignItems: 'center', color: MP.textMuted }}>
            <SearchIcon sx={{ fontSize: 22 }} />
          </Box>
          <InputBase
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. CBC, Vitamin D, KFT, Hemogram, HbA1c…"
            sx={{
              flex: 1,
              fontSize: 16,
              fontWeight: 500,
              color: MP.dark,
              px: 1.5,
              py: 1.75,
              fontFamily: DISPLAY,
            }}
          />
          {q && (
            <IconButton size="small" onClick={() => setQ('')} sx={{ mr: 1 }}>
              <ClearIcon sx={{ fontSize: 18, color: MP.textMuted }} />
            </IconButton>
          )}
        </Box>

        {/* Quick chips */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.75, flexWrap: 'wrap', rowGap: 1 }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: MP.textMuted,
              alignSelf: 'center',
              mr: 0.5,
            }}
          >
            Quick
          </Typography>
          {QUICK_SEARCHES.map((s) => (
            <Box
              key={s}
              onClick={() => setQ(s)}
              sx={{
                fontFamily: MONO,
                fontSize: 11.5,
                fontWeight: 700,
                color: MP.tealDark,
                bgcolor: MP.tealLight,
                border: `1px solid ${MP.tealMid}`,
                borderRadius: '20px',
                px: 1.25,
                py: 0.5,
                cursor: 'pointer',
                transition: 'all 0.14s',
                '&:hover': { bgcolor: MP.teal, color: MP.white, borderColor: MP.teal },
              }}
            >
              {s}
            </Box>
          ))}
        </Stack>
      </Box>

      <EdosResultsList
        tests={tests}
        loading={active.isLoading || active.isFetching}
        error={active.isError ? active.error : null}
        pagination={pagination}
        onPageChange={setPage}
        onSelectTest={setSelectedTest}
        emptyTitle={isSearching ? `No tests match "${debouncedQ}"` : 'No tests in directory'}
        emptySub="Try a shorter keyword, different alias, or browse by Department / Speciality from the sidebar."
      />
    </Stack>
  );
};

export default EdosTests;
